import type { ProductAddItemModel } from '@/api/system/model/productAddItemModel'
import type { UserInformationModel } from '@/api/model/userInformationMode'
import type { UserLoginInformationModel } from '@/api/system/model/userLoginInformationModel'
import type { AccountInfo } from '@azure/msal-common'
import type { CodesDetail } from '@/api/model/basicTypes'
import type { SendFolderInformationModel } from '@/api/model/sendMailModel'
import type { MultiSendCartInformation } from '@/api/multi-send-cart/model/multiSendCartModel'

import { useJSON } from '.'

export const USER_INFORMATION_KEY = 'user_information'
export const USER_LOGIN_INFORMATION_KEY = 'user_login_information'
export const CODES = 'codes'
export const PRODUCT_ADDITEM_KEY = 'product_add_item'
export const ACCOUNT = 'account'
export const SEND_FOLDER_INFORMATION = 'send_folder_information'
export const MULTI_SEND_CART_SORT_INFO = 'multiSendCartSortInfo'

export const storageKeys = {
  // ID TOKEN
  ID_TOKEN: 'id_token',
  // ドキュメントリスト
  DOCUMENT_LIST_KEY: 'document_list_key',
  CONFIG_HEADER_AUTHORIZATION: 'config_headers_authorization',
  CORPORATORS_INFO: 'corporators_info',
}

export interface UseStorage {
  // 共通メソッド
  saveDataToStorage: <T>(key: string, data: T) => void
  loadDataFromStorage: <T>(key: string) => T | undefined
  removeDataFromStorage: (key: string) => void

  // トークンから解析したユーザー情報
  saveUserInformation: (data: UserInformationModel) => void
  loadUserInformation: () => UserInformationModel
  removeUserInformation: () => void

  // ユーザーログイン情報
  saveUserLoginInformation: (loginInformation: UserLoginInformationModel) => void

  loadUserLoginInformation: (
    key?: typeof USER_LOGIN_INFORMATION_KEY
  ) => UserLoginInformationModel | undefined

  removeLoginInformation: (key: typeof USER_LOGIN_INFORMATION_KEY) => void

  // コード一覧情報
  saveCodesInformation: (codes: CodesDetail[]) => void
  loadCodesInformation: (key?: typeof CODES) => CodesDetail[] | undefined

  // 商品登録
  saveProductAddItem: (productAddItem: ProductAddItemModel) => void
  loadProductAddItem: (
    key?: typeof PRODUCT_ADDITEM_KEY
  ) => ProductAddItemModel | undefined
  removeProductAddItem: (key: typeof PRODUCT_ADDITEM_KEY) => void
  saveAccount: (account: AccountInfo) => void
  loadAccount: (key?: typeof ACCOUNT) => AccountInfo | undefined

  //受信・送信済みフォルダ
  saveSendFolderInformation: (sendFolderInformation: SendFolderInformationModel) => void
  loadSendFolderInformation: (
    key: typeof SEND_FOLDER_INFORMATION
  ) => SendFolderInformationModel | undefined

  //一括送信カートソート情報
  saveMultiSendCartInformation: (sortInfos: MultiSendCartInformation) => void
  loadMultiSendCartInformation: (key?: typeof MULTI_SEND_CART_SORT_INFO) => MultiSendCartInformation | undefined
}

const { toJSON, parseJSON } = useJSON()

/**
 * @description ローカルストレージにデータを保存するフック
 * @returns
 */
export const useStorage = (): UseStorage => {
  const saveDataToStorage = <T>(key: string, data: T): void => {
    sessionStorage.setItem(key, toJSON<T>(data))
  }

  const loadDataFromStorage = <T>(key: string): T | undefined => {
    const jsonData = sessionStorage.getItem(key)
    return jsonData ? parseJSON<T>(jsonData) : undefined
  }

  const removeDataFromStorage = (key: string): void => {
    sessionStorage.removeItem(key)
  }

  const saveUserInformation = (data: UserInformationModel): void => {
    sessionStorage.setItem(USER_INFORMATION_KEY, toJSON<UserInformationModel>(data))
  }

  const loadUserInformation = (): UserInformationModel => {
    const text: string | null = sessionStorage.getItem(USER_INFORMATION_KEY)

    return (text ? parseJSON<UserInformationModel>(text) : {}) as UserInformationModel
  }

  const removeUserInformation = (): void => {
    sessionStorage.removeItem(USER_INFORMATION_KEY)
  }

  const saveUserLoginInformation = (
    loginInformation: UserLoginInformationModel
  ): void => {
    sessionStorage.setItem(
      USER_LOGIN_INFORMATION_KEY,
      toJSON<UserLoginInformationModel>(loginInformation)
    )
  }

  const loadUserLoginInformation = (
    key?: typeof USER_LOGIN_INFORMATION_KEY
  ): UserLoginInformationModel | undefined => {
    const text: string | null = sessionStorage.getItem(key ?? USER_LOGIN_INFORMATION_KEY)

    if (!text) {
      return undefined
    }

    return parseJSON<UserLoginInformationModel>(text)
  }

  const removeLoginInformation = (key: typeof USER_LOGIN_INFORMATION_KEY): void => {
    sessionStorage.removeItem(key)
  }

  const saveCodesInformation = (codes: CodesDetail[]): void => {
    sessionStorage.setItem(CODES, toJSON<CodesDetail[]>(codes))
  }

  const loadCodesInformation = (key?: typeof CODES): CodesDetail[] | undefined => {
    const text: string | null = sessionStorage.getItem(key ?? CODES)

    if (!text) {
      return undefined
    }

    return parseJSON<CodesDetail[]>(text)
  }

  const saveProductAddItem = (productAdditem: ProductAddItemModel): void => {
    sessionStorage.setItem(
      PRODUCT_ADDITEM_KEY,
      toJSON<ProductAddItemModel>(productAdditem)
    )
  }

  const loadProductAddItem = (
    key?: typeof PRODUCT_ADDITEM_KEY
  ): ProductAddItemModel | undefined => {
    const text: string | null = sessionStorage.getItem(key ?? PRODUCT_ADDITEM_KEY)

    if (!text) {
      return undefined
    }

    return parseJSON<ProductAddItemModel>(text)
  }

  const removeProductAddItem = (key: typeof PRODUCT_ADDITEM_KEY): void => {
    sessionStorage.removeItem(key)
  }

  const saveAccount = (account: AccountInfo): void => {
    sessionStorage.setItem(ACCOUNT, toJSON<AccountInfo>(account))
  }

  const loadAccount = (key?: typeof ACCOUNT): AccountInfo | undefined => {
    const text: string | null = sessionStorage.getItem(key ?? ACCOUNT)

    if (!text) {
      return undefined
    }

    return parseJSON<AccountInfo>(text)
  }

  const saveSendFolderInformation = (
    sendFolderInformation: SendFolderInformationModel
  ): void => {
    sessionStorage.setItem(
      SEND_FOLDER_INFORMATION,
      toJSON<SendFolderInformationModel>(sendFolderInformation)
    )
  }

  const loadSendFolderInformation = (
    key?: typeof SEND_FOLDER_INFORMATION
  ): SendFolderInformationModel | undefined => {
    const text: string | null = sessionStorage.getItem(key ?? SEND_FOLDER_INFORMATION)

    if (!text) {
      return undefined
    }

    return parseJSON<SendFolderInformationModel>(text)
  }

  //一括送信カートソート情報
  const saveMultiSendCartInformation = (
    sortInfo: MultiSendCartInformation
  ): void => {
    sessionStorage.setItem(
      MULTI_SEND_CART_SORT_INFO,
      toJSON<MultiSendCartInformation>(sortInfo)
    )
  }

  const loadMultiSendCartInformation = (
    key?: typeof MULTI_SEND_CART_SORT_INFO
  ): MultiSendCartInformation | undefined => {
    const text: string | null = sessionStorage.getItem(key ?? MULTI_SEND_CART_SORT_INFO)

    if (!text) {
      return undefined
    }

    return parseJSON<MultiSendCartInformation>(text)
  }

  return {
    saveDataToStorage,
    loadDataFromStorage,
    removeDataFromStorage,
    saveUserInformation,
    loadUserInformation,
    removeUserInformation,
    saveUserLoginInformation,
    loadUserLoginInformation,
    removeLoginInformation,
    saveCodesInformation,
    loadCodesInformation,
    saveProductAddItem,
    loadProductAddItem,
    removeProductAddItem,
    saveAccount,
    loadAccount,
    saveSendFolderInformation,
    loadSendFolderInformation,
    saveMultiSendCartInformation,
    loadMultiSendCartInformation,
  }
}

export default useStorage
